import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });

    // Log to external service if needed
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    // Clear caches and reload
    const reloadPage = () => {
      (window as any).location.reload();
    };

    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        return Promise.all(
          cacheNames.map((cacheName) => {
            console.log('Clearing cache:', cacheName);
            return caches.delete(cacheName);
          })
        );
      }).then(() => {
        // Clear localStorage user data that might be corrupted
        const shouldClearUser = window.confirm(
          'Deseja limpar seus dados de sessão? Isso pode resolver o problema, mas você precisará fazer login novamente.'
        );
        if (shouldClearUser) {
          localStorage.removeItem('user');
        }
        reloadPage();
      }).catch((error) => {
        console.error('Error clearing caches:', error);
        reloadPage();
      });
    } else {
      reloadPage();
    }
  };

  handleClearAndReload = () => {
    // Clear all app data and reload
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        return Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
      }).catch((error) => {
        console.error('Error clearing caches:', error);
      });
    }
    localStorage.clear();
    sessionStorage.clear();
    (window as any).location.href = '/manu/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h1 style={{ color: '#333', marginBottom: '20px' }}>Ops! Algo deu errado</h1>
            <p style={{ color: '#666', marginBottom: '30px' }}>
              A aplicação encontrou um erro inesperado. Isso pode ser causado por:
            </p>
            <ul style={{
              textAlign: 'left',
              color: '#666',
              marginBottom: '30px',
              paddingLeft: '20px'
            }}>
              <li>Cache desatualizado do navegador</li>
              <li>Dados de sessão corrompidos</li>
              <li>Nova versão da aplicação disponível</li>
            </ul>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Limpar Cache e Recarregar
              </button>
              <button
                onClick={this.handleClearAndReload}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Resetar Tudo
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: '30px',
                textAlign: 'left',
                backgroundColor: '#f9f9f9',
                padding: '15px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}>
                  Detalhes do Erro (Desenvolvimento)
                </summary>
                <pre style={{
                  fontSize: '12px',
                  overflow: 'auto',
                  backgroundColor: '#fff',
                  padding: '10px',
                  borderRadius: '4px'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
